import { useEffect, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import TileCard from '../components/TileCard';

/**
 * Compliance expiration snapshot — Driver's TDLR + Driver's License (or
 * any configured compliance item). Pulled from /api/compliance-items
 * scoped to the logged-in user's employee record. Each row shows the
 * computed_status (current / expiring_soon / expired / pending) with a
 * colored chip + expiration date.
 *
 * Props:
 *   userId       (optional) — employee entity_id. Defaults to the logged-in
 *                              user from AuthContext so layouts don't need
 *                              to thread user IDs around.
 *   categories   (optional) — array of category_name substrings to filter
 *                               to. If omitted, show all items.
 *
 * If the user has no compliance items at all, renders the "no items"
 * empty state. If they have items but none match the category filter,
 * show all active items (don't surprise drivers with a blank tile).
 */
export default function ComplianceExpirationTile({ userId: userIdProp, categories }) {
  const { user } = useAuth();
  const userId = userIdProp || user?.id;

  const [state, setState] = useState({ loading: true, error: null, items: null });

  useEffect(() => {
    if (!userId) {
      setState({ loading: false, error: null, items: [] });
      return;
    }
    let cancelled = false;
    api.getComplianceItems({ entity_type: 'employee', entity_id: userId, all: 'true' })
      .then(rows => {
        if (cancelled) return;
        const arr = Array.isArray(rows) ? rows : (rows?.items || []);
        setState({ loading: false, error: null, items: arr });
      })
      .catch(err => {
        if (!cancelled) setState({ loading: false, error: err?.message || 'failed', items: null });
      });
    return () => { cancelled = true; };
  }, [userId]);

  const all = state.items || [];
  let filtered = all;
  if (categories && categories.length > 0) {
    const needles = categories.map(c => c.toLowerCase());
    filtered = all.filter(item =>
      needles.some(n => (item.category_name || '').toLowerCase().includes(n)
                     || (item.title || '').toLowerCase().includes(n))
    );
    // If the filter hides everything the user has, fall back to showing all.
    if (filtered.length === 0 && all.length > 0) filtered = all;
  }

  const isEmpty = !state.loading && !state.error && filtered.length === 0;

  return (
    <TileCard
      title="Licenses & Compliance"
      loading={state.loading}
      error={state.error}
      empty={isEmpty}
      emptyLabel="No compliance items on file"
    >
      <div className="dash-list">
        {filtered.slice(0, 4).map(item => (
          <div key={item.id} className="dash-lb-row">
            <span className="dash-lb-name">
              {item.title || item.category_name || 'Compliance item'}
              <div className="dash-list-sub" style={{ marginTop: 2 }}>
                {formatExpiration(item.expiration_date)}
              </div>
            </span>
            <StatusChip status={item.computed_status} />
          </div>
        ))}
      </div>
    </TileCard>
  );
}

function StatusChip({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.unknown;
  return (
    <span className="dash-list-meta" style={{
      color: style.color,
      background: style.bg,
      border: `1px solid ${style.border}`,
    }}>
      {style.label}
    </span>
  );
}

const STATUS_STYLES = {
  current:        { label: 'Current',   color: '#065F46', bg: '#D1FAE5', border: '#10B981' },
  expiring_soon:  { label: 'Soon',      color: '#92400E', bg: '#FEF3C7', border: '#F59E0B' },
  expired:        { label: 'Expired',   color: '#991B1B', bg: '#FEE2E2', border: '#DC2626' },
  pending:        { label: 'Pending',   color: '#1E40AF', bg: '#DBEAFE', border: '#3B82F6' },
  not_applicable: { label: 'N/A',       color: '#6B7280', bg: '#F3F4F6', border: '#D1D5DB' },
  unknown:        { label: '—',         color: '#6B7280', bg: '#F3F4F6', border: '#D1D5DB' },
};

function formatExpiration(dateStr) {
  if (!dateStr) return 'No expiration set';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return `Expires ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  } catch {
    return `Expires ${dateStr}`;
  }
}
